import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { expenseAPI } from '../../api/expenses';
import { vehicleAPI } from '../../api/vehicles';
import { tripAPI } from '../../api/trips';
import { Vehicle, Trip } from '../../types/entities';
import toast from 'react-hot-toast';
import {
  SearchableCombobox,
  createVehicleOptions,
  createTripOptions,
} from '../../components/UI/SearchableCombobox';
import { DocumentUpload, DocumentList } from '../../components/Documents';
import CurrencyInput from '../../components/UI/CurrencyInput';

interface ExpenseFormData {
  date: string;
  type: string;
  vehicleId: number | null;
  tripId: number | null;
  amount: string;
  quantity: string;
  unitPrice: string;
  description: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

export const ExpenseForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [createdExpenseId, setCreatedExpenseId] = useState<number | null>(null);
  const [documentRefreshKey, setDocumentRefreshKey] = useState(0);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      type: 'FUEL',
      vehicleId: null,
      tripId: null,
      amount: '',
      quantity: '',
      unitPrice: '',
      description: '',
    },
  });

  const expenseType = useWatch({ control, name: 'type' });
  const quantity = useWatch({ control, name: 'quantity' });
  const unitPrice = useWatch({ control, name: 'unitPrice' });

  // Create combobox options
  const vehicleOptions = useMemo(() => createVehicleOptions(vehicles), [vehicles]);
  const tripOptions = useMemo(() => createTripOptions(trips), [trips]);

  // Auto-calculate amount for FUEL
  useEffect(() => {
    if (expenseType === 'FUEL' && quantity && unitPrice) {
      const calculatedAmount = parseFloat(quantity) * parseFloat(unitPrice);
      if (!isNaN(calculatedAmount)) {
        setValue('amount', calculatedAmount.toString());
      }
    }
  }, [expenseType, quantity, unitPrice, setValue]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      loadExpense(parseInt(id));
    }
  }, [id, isEditMode]);

  const loadInitialData = async () => {
    try {
      const [vehiclesData, tripsData] = await Promise.all([
        vehicleAPI.getAll(),
        tripAPI.getAll(),
      ]);
      setVehicles(vehiclesData.filter((v) => v.status === 'ACTIVE'));
      setTrips(tripsData);
    } catch (error) {
      toast.error(t('errors.unknown'));
    }
  };

  const loadExpense = async (expenseId: number) => {
    try {
      const expense = await expenseAPI.getById(expenseId);
      setValue('date', expense.date.split('T')[0]);
      setValue('type', expense.type);
      setValue('vehicleId', expense.vehicleId || null);
      setValue('tripId', expense.tripId || null);
      setValue('amount', expense.amount?.toString() || '');
      setValue('quantity', expense.quantity?.toString() || '');
      setValue('unitPrice', expense.unitPrice?.toString() || '');
      setValue('description', expense.description || '');
    } catch (error) {
      toast.error(t('expenses.loadError'));
      navigate('/expenses');
    }
  };

  const onSubmit = async (data: ExpenseFormData) => {
    // Validation based on type
    if (data.type === 'FUEL') {
      if (!data.vehicleId || !data.quantity || !data.unitPrice) {
        toast.error(t('validation.required'));
        return;
      }
    } else if (data.type === 'REPAIR') {
      if (!data.vehicleId || !data.amount || !data.description) {
        toast.error(t('validation.required'));
        return;
      }
    } else if (data.type === 'OTHER') {
      if (!data.amount || (!data.vehicleId && !data.tripId)) {
        toast.error(t('validation.required'));
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        date: data.date,
        type: data.type,
        vehicleId: data.vehicleId || undefined,
        tripId: data.tripId || undefined,
        amount: data.amount ? parseFloat(data.amount) : undefined,
        quantity: data.quantity ? parseFloat(data.quantity) : undefined,
        unitPrice: data.unitPrice ? parseFloat(data.unitPrice) : undefined,
        description: data.description || undefined,
      };

      let expenseId: number;
      if (isEditMode && id) {
        await expenseAPI.update(parseInt(id), payload);
        expenseId = parseInt(id);
        toast.success(t('expenses.updateSuccess'));
      } else {
        const newExpense = await expenseAPI.create(payload);
        expenseId = newExpense.id;
        setCreatedExpenseId(expenseId);
        toast.success(t('expenses.createSuccess'));
      }
      
      // Don't navigate immediately - allow user to upload documents
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEditMode ? t('expenses.editExpense') : t('expenses.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('expenses.date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              {...register('date', { required: t('validation.required') })}
              className="input-field"
            />
            {errors.date && (
              <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('expenses.type')} <span className="text-red-500">*</span>
            </label>
            <select
              {...register('type', { required: t('validation.required') })}
              className="input-field"
            >
              <option value="FUEL">{t('expenses.types.FUEL')}</option>
              <option value="REPAIR">{t('expenses.types.REPAIR')}</option>
              <option value="OTHER">{t('expenses.types.OTHER')}</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('expenses.vehicle')} {expenseType !== 'OTHER' && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name="vehicleId"
              control={control}
              render={({ field }) => (
                <SearchableCombobox
                  options={vehicleOptions}
                  value={field.value}
                  onChange={(val) => field.onChange(val as number | null)}
                  placeholder={t('trips.selectVehicle')}
                />
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('expenses.trip')}</label>
            <Controller
              name="tripId"
              control={control}
              render={({ field }) => (
                <SearchableCombobox
                  options={tripOptions}
                  value={field.value}
                  onChange={(val) => field.onChange(val as number | null)}
                  placeholder={t('expenses.selectTrip')}
                />
              )}
            />
          </div>
        </div>

        {/* FUEL specific fields */}
        {expenseType === 'FUEL' && (
          <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('expenses.quantity')} (L) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                {...register('quantity')}
                className="input-field"
                placeholder="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('expenses.unitPrice')} <span className="text-red-500">*</span>
              </label>
              <Controller
                name="unitPrice"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="25.000"
                    className="input-field"
                  />
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.total')}</label>
              <div className="input-field bg-gray-100 text-gray-700">
                {quantity && unitPrice
                  ? formatCurrency(parseFloat(quantity) * parseFloat(unitPrice))
                  : '0'}{' '}
                đ
              </div>
            </div>
          </div>
        )}

        {/* REPAIR and OTHER fields */}
        {(expenseType === 'REPAIR' || expenseType === 'OTHER') && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('expenses.amount')} <span className="text-red-500">*</span>
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="500.000"
                  className="input-field"
                />
              )}
            />
          </div>
        )}

        {/* Description for REPAIR */}
        {expenseType === 'REPAIR' && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('expenses.description')} <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('description')}
              className="input-field"
              rows={3}
              placeholder={t('expenses.description')}
            />
          </div>
        )}

        {/* Description for OTHER */}
        {expenseType === 'OTHER' && (
          <div>
            <label className="block text-sm font-medium mb-1">{t('expenses.description')}</label>
            <textarea
              {...register('description')}
              className="input-field"
              rows={3}
              placeholder={t('expenses.description')}
            />
          </div>
        )}

        {/* Documents Upload */}
        <div>
          <h3 className="text-md font-semibold mb-3">{t('documents.expenseDocuments')}</h3>
          <DocumentUpload
            entityType="expense"
            entityId={isEditMode && id ? parseInt(id) : createdExpenseId}
            documentType="EXPENSE_INVOICE"
            disabled={loading}
            onUploadComplete={() => setDocumentRefreshKey(prev => prev + 1)}
          />
          
          {/* Show existing documents when editing */}
          {isEditMode && id && (
            <div className="mt-4">
              <DocumentList
                entityType="expense"
                entityId={parseInt(id)}
                canDelete={true}
                refreshTrigger={documentRefreshKey}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/expenses')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          {createdExpenseId && !isEditMode && (
            <button
              type="button"
              onClick={() => navigate('/expenses')}
              className="btn-primary"
            >
              {t('common.done')}
            </button>
          )}
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};
